import { useParams, useSearchParams } from "react-router-dom";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import { useDispatch } from "react-redux";
import { AssignFormRequest, MemberFormStatus, SaveAnswerRequest } from "clinical-forms-experience-client";
import { LoadingHelper } from "../../components/loader/LoadingHelper";

// Custom hook to encapsulate React hooks
export const useAssessmentParams = () => {
  const [searchParams] = useSearchParams();
  const { patientId, memberFormId, assessmentTypeId } = useParams<{
    patientId: string;
    memberFormId: string;
    assessmentTypeId: string;
  }>();
  const assessmentId = searchParams.get("assessmentId");
  const editPgis = Boolean(searchParams.get("editPgis"));
  const pastAssessment = Boolean(searchParams.get("pastAssessment"));

  return { patientId, assessmentId, memberFormId, assessmentTypeId, editPgis, pastAssessment };
};

// Initialize APIs
const clinicalformsApi = createClinicalFormsApi();

// GET clinical form
export const getOneClinicalForm = async (assessmentId: string) => {
    try {
      const clinicalFormsResponse =
        await clinicalformsApi.clinicalFormsControllerGetClinicalForm(assessmentId);
      return clinicalFormsResponse;
    } catch (error) {
      console.error("Error in fetching clilnical forms", error);
    }
  };

// GET clinical forms
export const getAllClinicalForms = async () => {
  try {
    const clinicalFormsResponse =
      await clinicalformsApi.clinicalFormsControllerGetClinicalForms(
        ClinicalFormsConstants.assessmentType
      );
    return clinicalFormsResponse;
  } catch (error) {
    console.error("Error in fetching clilnical forms", error);
    return null;
  }
};

// GET Member Form
export const getOneMemberForm = async (memberFormId: string) => {
  try {
    const response =
      await clinicalformsApi.clinicalFormsControllerGetMemberForm(memberFormId);
    return response;
  } catch (error) {
    console.error("Error in fetching member form", error);
  }
};

// GET Member Forms
export const getAllMemberForms = async (patientId: string) => {
  try {
    const response =
      await clinicalformsApi.clinicalFormsControllerGetMemberForms(
        patientId,
        ClinicalFormsConstants.assessmentType
      );
    return response;
  } catch (error) {
    console.error("Error in fetching member form", error);
  }
};
// GET Member Form Answers
export const getMemberFormAnswers = async (memberFormId: string) => {
  try {
    const response =
      await clinicalformsApi.clinicalFormsControllerGetMemberAnswers(memberFormId);
    return response;
  } catch (error) {
    console.error("Error in fetching member form answers", error);
  }
};

//
export const getAllMemberFormsStatuses = async (patientId: string) => {
  try {
    const response = await clinicalformsApi.clinicalFormsControllerGetMemberFormStatuses(
        patientId,
        ClinicalFormsConstants.assessmentType
      );
    return response;
  } catch (error) {
    console.error('Error in fetching statuses of member forms')
  }
};

// Assign clinical form
export const assignClinicalForm = async (
  clinicalFormId: string,
  patientId: string,
  assessmentId: string | null
) => {
  try {
    const request: AssignFormRequest = {
      formId: clinicalFormId,
      source: assessmentId ? "appointment" : "manual",
      sourceRefId: undefined,
    };
    const response =
      await clinicalformsApi.clinicalFormsControllerAssignClinicalForm(
        request,
        patientId
      );
    return response;
  } catch (error) {
    console.error("Error assigning clinical form: ", error);
  }
};

export const saveMemberAnswer = async (
  request: SaveAnswerRequest,
  memberFormId: string,
  questionId: string
  ) => {
  try {
    const response =
      await clinicalformsApi.clinicalFormsControllerSaveMemberAnswer(
        request,
        memberFormId,
        questionId
      );
    return response;
  } catch (error) {
    console.error("Error saving member answer: ", error);
  }
};

export const updateMemberFormStatus = async (
  status: string,
  memberFormId: string,
  reason?: string
) => {
  try {
    const response =
      await clinicalformsApi.clinicalFormsControllerUpdateMemberFormStatus(
        { value: status, reason },
        memberFormId
      );
    return response;
  } catch (error) {
    console.error("Error updating member form status: ", error);
  }
}

export const getPgisForMemberForm = async (memberFormId: string) => {
  LoadingHelper.showLoader("main");
  try {
    const response = await clinicalformsApi.clinicalFormsControllerGetPgisForMemberForm(memberFormId);
    return response;
  } catch (error) {
    console.error("Error fetching Pgis for member form ", error);
  } finally {
    LoadingHelper.hideLoader("main");
  }
}

